import { FC } from 'react';
import {
  Box,
  Button,
  Switch,
  OutlinedInput,
  InputAdornment,
  FormControlLabel,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useFilters } from './hooks/useFilters';
import { Select, TOOLBAR_FIELD_GAP, toolbarFieldSx } from '@ui';
import { MultiselectSearchableField } from 'components/Multiselect';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import { theme } from '@store';
import { ColumnDensityFilter } from 'components/TablePreferences/ColumnDensityFilter';
import { WorklistTableColumnSelectionFilter } from 'components/TablePreferences/WorklistTableColumnSelectionFilter';

export const Filters: FC = () => {
  const {
    filters,
    handleSearchChange,
    handleToggleFiltersCard,
    handleOwnTasksOnlyChange,
    handlePartnerOnChange,
    handleProviderOnChange,
    providerOptions,
    partnerOptions,
    isFiltersCardOpen,
    appliedFiltersCount,
    handleUpdatePreferences,
    preferences,
    searchQuery,
  } = useFilters();

  return (
    <Box
      sx={{
        width: '100%',
        padding: '1rem',
        display: 'flex',
        justifyContent: 'space-between',
        bgcolor: 'white',
        border: theme => `1px solid ${theme.palette.grey[300]}`,
        borderBottom: 'none',
        alignItems: 'center',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: TOOLBAR_FIELD_GAP,
          width: '100%',
          boxSizing: 'border-box',
          alignItems: 'center',
        }}
      >
        <OutlinedInput
          id='searchField'
          placeholder='Search patient'
          onChange={handleSearchChange}
          value={searchQuery}
          sx={toolbarFieldSx}
          startAdornment={
            <InputAdornment sx={{ padding: 0 }} position='start'>
              <SearchIcon />
            </InputAdornment>
          }
        />
        <Select
          name='partner'
          size='medium'
          label='Partner'
          onChange={handlePartnerOnChange}
          value={filters.partner_id}
          options={partnerOptions}
          clearLabel='All'
          clearable
          sx={{
            ...toolbarFieldSx,
            display: 'flex',
            justifyContent: 'center',
          }}
        />
        <MultiselectSearchableField
          options={providerOptions}
          value={
            Array.isArray(filters.provider_ids) ? filters?.provider_ids : []
          }
          name={'provider_ids'}
          label={'Provider'}
          handleChange={values => handleProviderOnChange(values)}
          optional
          hideHelperText
          sx={toolbarFieldSx}
        />
      </Box>

      <Box sx={{ display: 'flex', pl: '1rem' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            minWidth: '290px',
            [theme.breakpoints.down('md')]: {
              minWidth: '250px',
            },
            textWrap: 'nowrap',
          }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={filters.own_tasks_only}
                onChange={handleOwnTasksOnlyChange}
              />
            }
            label='Only my tasks'
          />

          <WorklistTableColumnSelectionFilter hideText={isFiltersCardOpen} />
          <ColumnDensityFilter
            handleDensityChange={density => {
              handleUpdatePreferences({ density });
            }}
            selectedValue={preferences?.density}
            hideText={isFiltersCardOpen}
          />

          <Button
            sx={{
              display: 'flex',
              gap: 0.5,
              fontWeight: 'medium',
              fontSize: '15px',
              ...(isFiltersCardOpen && {
                bgcolor: theme.palette.selectedTableRowBg.main,
              }),
            }}
            variant='text'
            onClick={handleToggleFiltersCard}
          >
            <FilterAltIcon />
            <Typography
              sx={{
                fontSize: '15px',
                fontWeight: 'medium',
                display: {
                  lg: 'initial',
                  md: isFiltersCardOpen ? 'none' : 'initial',
                  sm: 'none',
                },
              }}
            >
              FILTERS
            </Typography>
            <Typography sx={{ fontSize: '15px', fontWeight: 'medium' }}>
              {appliedFiltersCount > 0 ? `(${appliedFiltersCount})` : ''}
            </Typography>
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
